"use client";

import {
  IconBarcode,
  IconBowlChopsticks,
  IconBowlChopsticksFilled,
  IconHome,
  IconHomeFilled,
  IconShoppingCart,
  IconShoppingCartFilled,
} from "@tabler/icons-react";
import { useStore } from "@tanstack/react-store";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import React, { Suspense } from "react";
import { shoppingProductsIndexStore } from "../stores/shopping-products";
import { localStorage } from "../utils/local-storage";
import { useProductMealCountToday } from "../utils/meals";

export const navigationItems = [
  {
    name: "Home",
    href: "/home/",
    icon: <IconHome className="size-6" />,
    activeIcon: <IconHomeFilled className="size-6" />,
  },
  {
    name: "Groceries",
    href: "/buy/",
    icon: <IconShoppingCart className="size-6" />,
    activeIcon: <IconShoppingCartFilled className="size-6" />,
  },
  {
    name: "Meals",
    href: "/track/",
    icon: <IconBowlChopsticks className="size-6" />,
    activeIcon: <IconBowlChopsticksFilled className="size-6" />,
  },
  // {
  //   name: "Workouts",
  //   href: "/workouts/",
  //   icon: <IconBarbell className="size-6" />,
  //   activeIcon: <IconBarbellFilled className="size-6" />,
  // },
];

export const actionItems = [
  {
    name: "Scan",
    href: "/scan/",
    icon: <IconBarcode className="size-6" />,
    activeIcon: <IconBarcode className="size-6" />,
  },
];

export function NavbarContent() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [activeTab, setActiveTab] = React.useState<string>(pathname || "/");
  const [visible, setVisible] = React.useState(true);
  const lastScrollY = React.useRef(0);
  const ticking = React.useRef(false);
  const shoppingItems = useStore(shoppingProductsIndexStore);
  const itemCount = shoppingItems.items.length;

  const trackCount = useProductMealCountToday() ?? 0;

  React.useEffect(() => {
    setActiveTab(window.location.pathname || "/");

    localStorage?.setItem(
      "lastPath",
      window.location.href.replace(window.location.origin, "") || "/home/"
    );
  }, [pathname, searchParams]);

  React.useEffect(() => {
    setVisible(true);
  }, [searchParams]);

  React.useEffect(() => {
    // We'll use window scroll events, but check different elements to find the scrollable one
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          // Get current scroll position from appropriate element
          const currentScrollY = window.scrollY || document.documentElement.scrollTop;

          // Determine if we're scrolling down
          const isScrollingDown = currentScrollY > lastScrollY.current;

          // Determine if we're near the bottom
          const scrollHeight = document.documentElement.scrollHeight;
          const windowHeight = window.innerHeight;
          const isNearBottom = currentScrollY + windowHeight >= scrollHeight - 80;

          // Check if we should show or hide the navbar
          if (isScrollingDown && !isNearBottom && currentScrollY > 50) {
            setVisible(false);
          } else {
            setVisible(true);
          }

          // Update last scroll position
          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    // Add scroll listener to window - this works regardless of which element is scrollable
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Clean up
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <nav
      className={`fixed z-[25] bottom-0 pointer-events-none
          flex w-full justify-center
          transition-transform duration-300 ease-in-out
          box-content pb-[env(safe-area-inset-bottom)]
          ${visible ? "translate-y-0 scale-100" : "translate-y-full scale-50"}
      `}
    >
      <div
        className="w-md max-w-md mx-4 mb-4 rounded-full pointer-events-auto
         flex items-center justify-between"
      >
        <ul
          className="flex gap-2 p-1 rounded-full 
          bg-neutral-600/50 text-white
          shadow-[inset_0_0_8px_rgba(0,0,0,0.10),inset_1px_1px_2px_rgba(255,255,255,0.5),inset_-1px_-1px_2px_rgba(255,255,255,0.5),1px_4px_16px_rgba(0,0,0,0.3)]
          backdrop-blur-xs"
        >
          {navigationItems.map((item) => (
            <li key={item.name}>
              <Link
                prefetch={false}
                href={item.href}
                className={`flex flex-col items-center justify-center  size-12 active:bg-white/20 rounded-full relative`}
              >
                <>{activeTab === item.href ? item.activeIcon : item.icon}</>
                {item.name === "Groceries" && itemCount > 0 && (
                  <span className="absolute top-0 right-1 min-w-4.5 h-4.5 bg-lime-500 text-[11px] dark:bg-lime-600 dark:text-black px-1 font-bold rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
                {item.name === "Meals" && trackCount > 0 && (
                  <span className="absolute top-0 right-1 min-w-4.5 h-4.5 bg-sky-500 text-[11px] dark:bg-sky-600 dark:text-black px-1 font-bold rounded-full flex items-center justify-center">
                    {trackCount}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
        <ul
          className="flex gap-2 p-1 rounded-full text-white
          bg-sky-500/80 
          shadow-[inset_0_0_8px_rgba(0,0,0,0.1),inset_1px_1px_2px_rgba(255,255,255,0.5),inset_-1px_-1px_2px_rgba(255,255,255,0.5),1px_4px_16px_rgba(0,0,0,0.3)]
          backdrop-blur-xs"
        >
          {actionItems.map((item) => (
            <li key={item.name}>
              <Link
                className="flex items-center justify-center size-12 active:bg-white/20 rounded-full"
                href={item.href}
              >
                {activeTab === item.href ? item.activeIcon : item.icon}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

export function Navbar() {
  return (
    <Suspense
      fallback={
        <nav className="fixed w-full h-[57px] md:h-[80px] dark:bg-black/60 bg-white/60 backdrop-blur-md z-[30] bottom-0 box-content pb-[env(safe-area-inset-bottom)]">
          <div className="animate-pulse h-full"></div>
        </nav>
      }
    >
      <NavbarContent />
    </Suspense>
  );
}
